import React, { useCallback, useState } from "react";
import styles from "./AuthorProfileSec1.module.css";
import { FiArrowUpRight } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { IoIosShareAlt } from "react-icons/io";
import { FaCalendarDays } from "react-icons/fa6";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import ShareModal from "@/Component/Module/HolidayDetail/ShareModal";
import { fetchBlogsByAuthor } from "@/services/api/blog";
import {
  buildAuthorBlogListUrl,
  getRegionPath,
  normalizeAuthorBlogsResponse,
} from "@/utils/blogHelpers";
import { withRegionPath } from "@/utils/session";

const AUTHOR_PAGE_SIZE = 12;

function getArticleShareUrl(slug = "") {
  const path = withRegionPath(`/blog/${slug}`);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

function InsightCard({ item, articleHref = "", onShare }) {
  const handleShare = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onShare?.(item);
  };

  return (
    <article className={styles.travelInsightCard}>
      <div className={styles.travelInsightImageWrap}>
        <img
          src={item.image || "/images/insight1.jpg"}
          alt={item.title}
          className={styles.travelInsightCardImage}
          onError={(event) => {
            event.currentTarget.src = "/images/insight1.jpg";
          }}
        />
        {item.date ? (
          <span className={styles.travelInsightDateBadge}>
            <FaCalendarDays size={11} color="#D92027" />
            {item.date}
          </span>
        ) : null}
      </div>

      <div className={styles.travelInsightBody}>
        {item.category ? (
          <div className={styles.travelInsightCategoryTag}>
            {item.categoryIcon ? (
              <img
                src={item.categoryIcon}
                alt={item.category}
                className={styles.travelInsightCategoryIcon}
              />
            ) : null}
            <span>{item.category}</span>
          </div>
        ) : null}

        <h3 className={styles.travelInsightCardTitle}>{item.title}</h3>
        <p className={styles.travelInsightCardDescription}>{item.description}</p>

        <div className={styles.travelInsightFooter}>
          <div className={styles.travelInsightMetaLeft}>
            <span className={styles.travelInsightViews}>
              <IoEye size={13} color="#424753" />
              {item.views}
            </span>
            <button
              type="button"
              className={styles.travelInsightShareBtn}
              onClick={handleShare}
              aria-label="Share article"
            >
              <IoIosShareAlt size={18} color="#424753" />
            </button>
          </div>

          <a
            href={articleHref || withRegionPath(`/blog/${item.slug}`)}
            className={styles.travelInsightArrowBtn}
            aria-label="Read full article"
          >
            <FiArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function AuthorProfileSec1({
  authorSlug = "",
  authorName = "",
  authorImage = "",
  authorBio = "",
  articles = [],
  initialArticleMeta = null,
  country = "UAE",
  className = "",
}) {
  const [articleItems, setArticleItems] = useState(articles || []);
  const [articleTotalCount, setArticleTotalCount] = useState(
    initialArticleMeta?.totalCount || articles.length || 0
  );
  const [articlePageIndex, setArticlePageIndex] = useState(
    initialArticleMeta?.pageIndex || 1
  );
  const [articleTotalPages, setArticleTotalPages] = useState(
    initialArticleMeta?.totalPages || 1
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  const displayName = authorName || "Author";
  const displayImage = authorImage || "/images/author-avatar.jpg";
  const displayBio =
    authorBio ||
    "Explore travel stories, destination guides, and holiday inspiration from this author.";

  const getArticleHref = useCallback(
    (slug = "") => getRegionPath(country, `/blog/${slug}`),
    [country]
  );

  const showLoadMore =
    articleTotalCount > AUTHOR_PAGE_SIZE && articlePageIndex < articleTotalPages;

  const loadMoreHref = buildAuthorBlogListUrl(country, {
    authorSlug,
    page: articlePageIndex + 1,
  });

  const handleShareItem = (item) => {
    if (!item?.slug) return;

    setShareTarget({
      title: item.title || "",
      url: getArticleShareUrl(item.slug),
    });
  };

  const handleLoadMore = async () => {
    if (articlePageIndex >= articleTotalPages) return;

    setIsLoadingMore(true);
    try {
      const response = await fetchBlogsByAuthor({
        authorSlug,
        pageSize: AUTHOR_PAGE_SIZE,
        pageIndex: articlePageIndex + 1,
      });
      const normalized = normalizeAuthorBlogsResponse(response);

      setArticleItems((prev) => [...prev, ...normalized.items]);
      setArticleTotalCount(normalized.totalCount);
      setArticlePageIndex(normalized.pageIndex);
      setArticleTotalPages(normalized.totalPages);
    } catch (error) {
      console.error("Author articles load more error:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLoadMoreClick = (event) => {
    event.preventDefault();
    handleLoadMore();
  };

  return (
    <>
      <Breadcrumbs />
      <section className={`${styles.authorProfileSection} standard-padding ${className}`.trim()}>
        <div className={styles.authorProfileContainer}>
          <h1 className={`${styles.authorMainTitle} standard-heading`}>
            Author Profile - {displayName}
          </h1>

          <div className={styles.authorBioCard}>
            <div className={styles.authorAvatarWrap}>
              <img
                src={displayImage}
                alt={displayName}
                className={styles.authorAvatar}
                onError={(event) => {
                  event.currentTarget.src = "/images/author-avatar.jpg";
                }}
              />
            </div>
            <div className={styles.authorBioContent}>
              <h2 className={styles.authorBioHeading}>About the Author</h2>
              <p className={styles.authorBioText}>{displayBio}</p>
            </div>
          </div>

          <div className={styles.authorArticlesHeader}>
            <h2 className="standard-heading">Articles</h2>
          </div>

          {articleItems.length === 0 ? (
            <p className="nodata">No articles found for this author.</p>
          ) : (
            <>
              <div className={styles.blogGrid}>
                {articleItems.map((item, index) => (
                  <InsightCard
                    key={`${item.id}-${index}`}
                    item={item}
                    articleHref={getArticleHref(item.slug)}
                    onShare={handleShareItem}
                  />
                ))}
              </div>

              {(showLoadMore || isLoadingMore) && (
                <div className={styles.loadMoreWrap}>
                  {isLoadingMore ? (
                    <div className={styles.loadingWrapBottom}>
                      <img
                        src="/assets/loading.gif"
                        alt="Loading more articles"
                        className={styles.loadingGif}
                      />
                    </div>
                  ) : (
                    <a
                      href={loadMoreHref}
                      className={styles.loadMoreBtn}
                      onClick={handleLoadMoreClick}
                    >
                      Load More Blogs
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <ShareModal
          show={Boolean(shareTarget)}
          onHide={() => setShareTarget(null)}
          shareUrl={shareTarget?.url || ""}
          title={shareTarget?.title || ""}
        />
      </section>
    </>
  );
}
